import React, {
  useRef,
  useId,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react';
import { ChevronDownIc } from '@dsch/dd-icons';
import * as Styled from './Accordion.styled';
import { Spacer } from '../../../helpers/genericStyles';
import type { AccordionProps } from './Accordion.typed';
import withDefaultProps from '../hoc/withDefaultProps';
import withStringOrComponent from '../hoc/withStringOrComponent';
import { useTheme } from 'styled-components';
import { Title, type ITitle } from '../../Toolkit/InfoTitle/Title/Title';

type AccordionHeadingProps = Partial<ITitle> &
  React.ComponentPropsWithoutRef<'p'>;

type AccordionComponent = ForwardRefExoticComponent<
  AccordionProps & RefAttributes<HTMLDivElement>
> & {
  Heading: React.ComponentType<AccordionHeadingProps>;
};

const Accordion = forwardRef<HTMLDivElement, AccordionProps>((props, ref) => {
  const {
    isOpen,
    onChange,
    header,
    headerSpacerTop = <Spacer marginBottom={'M16'} />,
    headerSpacerBottom = <Spacer marginBottom={'M16'} />,
    bodySpacer = <Spacer marginBottom={'M24'} />,
    content,
    showDivider = true,
    dataTracking,
    accordionName,
  } = props;

  const heading = withStringOrComponent(header, Accordion.Heading);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const uniqueId = useId();

  const onKeyDown = (event: React.KeyboardEvent) => {
    const key = event.code.toLowerCase();
    const isFocussed = document.activeElement === headerRef.current;

    if (isFocussed && (key === 'enter' || key === 'space')) {
      event.preventDefault();
      onChange();
    }
  };

  return (
    <Styled.Accordion ref={ref} showDivider={showDivider}>
      {headerSpacerTop}
      <Styled.Header
        ref={headerRef}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        aria-controls={accordionName}
        aria-labelledby={uniqueId}
        onClick={onChange}
        onKeyDown={onKeyDown}
        data-tracking={dataTracking}
        data-testid={'accordion-header'}
      >
        <div id={uniqueId}>{heading}</div>

        <Styled.ChevronIconWrapper isOpen={isOpen}>
          <ChevronDownIc
            width="20"
            height="20"
            color={theme.donedeal.colors.GREY_DARKER}
            data-testid={'accordion-chevron'}
          />
        </Styled.ChevronIconWrapper>
      </Styled.Header>
      {headerSpacerBottom}
      <Styled.Body
        data-testid={'accordion-body'}
        isOpen={isOpen}
        id={accordionName}
      >
        {content}
      </Styled.Body>
      {bodySpacer}
    </Styled.Accordion>
  );
}) as AccordionComponent;

Accordion.Heading = withDefaultProps(Title, {
  flip: false,
  shouldTruncate: false,
  variant: 'DEFAULT',
}) satisfies AccordionComponent['Heading'];

Accordion.displayName = 'Accordion';

export { Accordion };
